import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Button, Modal } from 'examkrackers-components'

import CheckRoundedIcon from 'assets/check-rounded.svg'
import StarsIcon from 'assets/stars.svg'
import TaskIcon from 'assets/task.svg'
import SaltyGradCap from 'assets/SaltyGradCap.svg'
import { EXTEND_SUBSCRIPTION_URL } from 'utils/courses'

export type LiveTutoringPromoModalProps = {
  open: boolean
  onClose: () => void
}

const BENEFITS = [
  {
    icon: CheckRoundedIcon,
    translationKey: 'freeTrial.liveTutoringPromoModal.benefit1'
  },
  {
    icon: StarsIcon,
    translationKey: 'freeTrial.liveTutoringPromoModal.benefit2'
  },
  {
    icon: TaskIcon,
    translationKey: 'freeTrial.liveTutoringPromoModal.benefit3'
  }
] as const

const LiveTutoringPromoModal = ({
  open,
  onClose
}: LiveTutoringPromoModalProps): JSX.Element => {
  const { t } = useTranslation()

  const handleViewPlans = () => {
    window.open(EXTEND_SUBSCRIPTION_URL, '_blank', 'noopener,noreferrer')
  }

  const handleBackToFreeTrial = () => {
    onClose()
  }

  return (
    <Modal
      open={open}
      handleClose={onClose}
      hasBorder
      data-testid='LiveTutoringPromoModal-Modal'
    >
      <MainWrapper data-testid='LiveTutoringPromoModal-MainWrapper'>
        <TextWrapper data-testid='LiveTutoringPromoModal-TextWrapper'>
          <Eyebrow data-testid='LiveTutoringPromoModal-Eyebrow'>
            {t('freeTrial.liveTutoringPromoModal.eyebrow')}
          </Eyebrow>
          <Title data-testid='LiveTutoringPromoModal-Title'>
            {t('freeTrial.liveTutoringPromoModal.title')}
          </Title>
        </TextWrapper>

        <Content data-testid='LiveTutoringPromoModal-Content'>
          <BenefitsList data-testid='LiveTutoringPromoModal-BenefitsList'>
            {BENEFITS.map((benefit, index) => (
              <BenefitItem
                key={benefit.translationKey}
                $alignItems={index === 0 ? 'flex-start' : 'center'}
              >
                <BenefitIcon src={benefit.icon} alt='' aria-hidden />
                <BenefitText>{t(benefit.translationKey)}</BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>

          <SaltyContainer data-testid='LiveTutoringPromoModal-SaltyContainer'>
            <SaltyImage
              data-testid='LiveTutoringPromoModal-SaltyImage'
              src={SaltyGradCap}
              alt=''
              aria-hidden
            />
          </SaltyContainer>
        </Content>

        <ButtonsContainer data-testid='LiveTutoringPromoModal-ButtonsContainer'>
          <ViewPlansButton
            color='primary'
            onClick={handleViewPlans}
            aria-label={t('freeTrial.liveTutoringPromoModal.viewPlans')}
            data-testid='LiveTutoringPromoModal-ViewPlansButton'
          >
            {t('freeTrial.liveTutoringPromoModal.viewPlans')}
          </ViewPlansButton>
          <BackToFreeTrialLink
            role='button'
            tabIndex={0}
            onClick={handleBackToFreeTrial}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleBackToFreeTrial()
              }
            }}
            aria-label={t('freeTrial.liveTutoringPromoModal.backToFreeTrial')}
            data-testid='LiveTutoringPromoModal-BackToFreeTrialLink'
          >
            {t('freeTrial.liveTutoringPromoModal.backToFreeTrial')}
          </BackToFreeTrialLink>
        </ButtonsContainer>
      </MainWrapper>
    </Modal>
  )
}

export default LiveTutoringPromoModal

const MainWrapper = styled.div`
  position: relative;
  margin: 0 48px 0px;
  max-width: 650px;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const Eyebrow = styled.div`
  font-family: Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  font-style: normal;
  color: #1a1a1a;
  margin-bottom: 24px;
`

const Title = styled.div`
  font-family: Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  font-style: normal;
  color: #1a1a1a;
  max-width: 520px;
  margin-bottom: 18px;
`

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  margin-bottom: 18px;
`

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
  max-width: 340px;
`

const BenefitItem = styled.div<{ $alignItems: 'flex-start' | 'center' }>`
  display: flex;
  align-items: ${({ $alignItems }) => $alignItems};
  gap: 8px;
`

const BenefitIcon = styled.img`
  width: 25px;
  height: 25px;
  flex-shrink: 0;
`

const BenefitText = styled.div`
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  font-style: normal;
  color: #444444;
  text-align: left;
`

const SaltyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const SaltyImage = styled.img`
  height: 153px;
  width: 128px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 42px;
  align-items: center;
  justify-content: center;
`

const ViewPlansButton = styled(Button)`
  display: flex;
  width: 126px;
  min-width: 126px;
  padding: 12px;
  justify-content: center;
`

const BackToFreeTrialLink = styled.div`
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  font-style: normal;
  color: #412312;
  text-decoration: underline;
  cursor: pointer;
`
